export type AppLocale = "ko" | "en";

export const KOREAN_APP_ORIGIN = "https://mypetroutine.petnow.io";
export const ENGLISH_APP_ORIGIN = "https://en.mypetroutine.petnow.io";

const USER_LOCALE_OVERRIDE_PREFIX = "mypetroutine:user-locale:";
const GUEST_LOCALE_OVERRIDE_KEY = "mypetroutine:guest-locale";
export const APP_LOCALE_CHANGED_EVENT = "mypetroutine:locale-changed";

const ENGLISH_PUBLIC_HOSTNAMES = new Set([
  "en.mypetroutine.petnow.io",
  "en.localhost",
]);

export function resolveAppLocaleFromHostname(hostname?: string | null): AppLocale {
  const normalized = String(hostname || "")
    .trim()
    .toLowerCase()
    .replace(/:\d+$/u, "");

  return ENGLISH_PUBLIC_HOSTNAMES.has(normalized) ? "en" : "ko";
}

export function resolveAppLocaleFromUrl(input?: string | URL | Location | null): AppLocale {
  if (!input) {
    return "ko";
  }

  try {
    const url = typeof input === "string"
      ? new URL(input, KOREAN_APP_ORIGIN)
      : new URL(input.href);
    const queryLocale = url.searchParams.get("lang");

    if (queryLocale === "en" || queryLocale === "ko") {
      return queryLocale;
    }

    return resolveAppLocaleFromHostname(url.hostname);
  } catch {
    return "ko";
  }
}

export function resolveCurrentAppLocale(): AppLocale {
  if (typeof window === "undefined") {
    return "ko";
  }

  try {
    const userId = window.localStorage.getItem("mypetroutine:authenticated-user-id");
    if (userId) {
      const override = window.localStorage.getItem(`${USER_LOCALE_OVERRIDE_PREFIX}${userId}`);
      if (override === "en" || override === "ko") {
        return override;
      }
    }

    const guestOverride = window.localStorage.getItem(GUEST_LOCALE_OVERRIDE_KEY);
    if (guestOverride === "en" || guestOverride === "ko") {
      return guestOverride;
    }
  } catch {
    // Privacy mode and blocked storage must not prevent the app from loading.
  }

  return resolveAppLocaleFromUrl(window.location);
}

export function setUserLocaleOverride(userId: string, locale: AppLocale) {
  if (typeof window === "undefined" || !userId) {
    return;
  }

  try {
    window.localStorage.setItem("mypetroutine:authenticated-user-id", userId);
    window.localStorage.setItem(`${USER_LOCALE_OVERRIDE_PREFIX}${userId}`, locale);
    window.dispatchEvent(new CustomEvent(APP_LOCALE_CHANGED_EVENT, { detail: { locale, userId } }));
  } catch {
    // Keep the current in-memory locale usable when browser storage is unavailable.
  }
}

export function setGuestLocaleOverride(locale: AppLocale) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(GUEST_LOCALE_OVERRIDE_KEY, locale);
    window.dispatchEvent(new CustomEvent(APP_LOCALE_CHANGED_EVENT, { detail: { locale } }));
  } catch {
    // Keep the current in-memory locale usable when browser storage is unavailable.
  }
}

export function clearUserLocaleOverride(userId?: string | null) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    const activeUserId = userId || window.localStorage.getItem("mypetroutine:authenticated-user-id");
    if (activeUserId) {
      window.localStorage.removeItem(`${USER_LOCALE_OVERRIDE_PREFIX}${activeUserId}`);
    }
    window.localStorage.removeItem("mypetroutine:authenticated-user-id");
    window.dispatchEvent(new CustomEvent(APP_LOCALE_CHANGED_EVENT, { detail: { locale: null, userId: activeUserId ?? null } }));
  } catch {
    // Ignore blocked storage during sign-out.
  }
}

export function resolveLocalizedAppOrigin(locale: AppLocale) {
  return locale === "en" ? ENGLISH_APP_ORIGIN : KOREAN_APP_ORIGIN;
}

export function isEnglishLocale(locale: AppLocale) {
  return locale === "en";
}
